import { Ribbon } from 'icons';
import { useTranslation } from 'react-i18next';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { formatDate } from '@/utils/date';
import { getDueDateColor, getDueDateString } from '@/utils/dueDate';
import { getProgressBarText } from '@/utils/generateProgressBarText';
import { generateProgressText } from '@/utils/generateProgressText';
import { itemByLanguage } from '@/utils/language';
import { getTimeString } from '@/utils/time';

import Constants, { routes } from 'constants/index';

import PlanCard from '../PlanCard';
import PlanCardIcon from '../PlanCardComponents/PlanCardIcon';
import PlanCardLabel from '../PlanCardComponents/PlanCardLabel';
import PlanCardSubHeader from '../PlanCardComponents/PlanCardSubHeader';
import PlanCardTag from '../PlanCardComponents/PlanCardTag';
import PlanCardTitle from '../PlanCardComponents/PlanCardTitle';
import PlanCardContent from '../PlanCardContent/PlanCardContent';

import { UserPlanStatus } from '@/_generated/api-clients/xp';

interface ReportsTeamMemberPlanCardProps {
  teamMemberId: string;
  userPlanStatus: UserPlanStatus;
}

const ReportsTeamMemberPlanCard: React.FC<ReportsTeamMemberPlanCardProps> = ({
  userPlanStatus,
  teamMemberId,
}) => {
  const { t } = useTranslation();

  const {
    category,
    completedSteps,
    completionDate,
    dueDate,
    planId,
    planName,
    planStatus,
    timeTakenInSeconds,
    totalSteps,
  } = userPlanStatus;

  const isComplete = planStatus === Constants.TRAINING_PLANS.COMPLETED;
  const dueDateTime = new Date(dueDate ?? '').getTime();
  const percentComplete =
    totalSteps && Math.round((completedSteps / totalSteps) * 100);
  const durationMins = Math.ceil(timeTakenInSeconds / 60);
  const timeSpentOnPlan = getTimeString(durationMins);

  return (
    <PlanCard
      category={category}
      dataTestId={'TeamMemberPlan'}
      link={routes.reports.planTeamMemberProgress({ planId, teamMemberId })}
      progress={!isComplete && totalSteps ? percentComplete : null}
    >
      <PlanCardContent>
        <PlanCardLabel>
          {t(mapCategoryToAliasTranslation(category))}
        </PlanCardLabel>
        <PlanCardTitle>{itemByLanguage(planName, '')}</PlanCardTitle>
        {isComplete ? (
          <PlanCardSubHeader className="success" data-testid="PlanTaskCount">
            {generateProgressText({
              completedStepCount: completedSteps,
              isComplete,
              timeSpentOnPlan,
              stepCount: totalSteps,
            })}
          </PlanCardSubHeader>
        ) : (
          <PlanCardSubHeader data-testid="PlanTaskCount">
            {getProgressBarText({
              completedStepCount: completedSteps,
              isComplete,
              percentComplete,
              stepCount: totalSteps,
              timeSpentOnPlan,
            })}
          </PlanCardSubHeader>
        )}
      </PlanCardContent>
      {!isComplete && !!dueDate && (
        <PlanCardTag
          color={getDueDateColor(dueDateTime)}
          text={getDueDateString(dueDateTime)}
          tooltip={t('TrainingPlans.accountability.due.dueDate', {
            dueDate: formatDate(dueDateTime),
          })}
        />
      )}
      {isComplete && (
        <PlanCardIcon
          icon={<Ribbon className="size-7!" />}
          tooltip={
            <>
              <strong>{t('TrainingPlans.statusOptions.completed')}:</strong>
              {completionDate ? formatDate(completionDate) : t('Generic.na')}
            </>
          }
        />
      )}
    </PlanCard>
  );
};

export default ReportsTeamMemberPlanCard;
